import * as Immutable from "immutable";
import Position from "./Position";
import dayjs from "dayjs";
import { Colors } from "../style/colors";
import { AlarmHandler } from "./AlarmHandler";
import {
  deviceTypes,
  deviceTypesInv,
  getManufacturer,
  realDeviceTypes,
  getV4Brand,
  getV3TypeIdForV4Type,
  isV4OnlyBrand,
  prettifyV4Type,
} from "./DeviceType";
import Account from "./Account";
import Scenario from "./Scenario";
import { getWhitelabel } from "../../env";
import { sortableDateTimeFormat } from "../style/formats";
import { consoleError } from "../log";
import { scenarioTriggers } from "./scenarios/scenarioTriggers";
import DeviceBatteryIcon from "../../components/DeviceBatteryIcon";
import { isSupportedFirmware } from "../supportedFirmware";
import { isV4CapabilityKnown } from "./v4DeviceCapabilities";
import { v4SystemPropertyLabel } from "./v4DeviceProperties";
import { DEVICE_TYPES } from "../constants";

export const activeState = {
  toActivate: 0,
  toConfigure: 1,
  active: 2,
};

export const READ_ONLY_FIELD_PREFIX = "#";
export const isReadOnlyField = (field) =>
  typeof field?.name === "string" && field.name.startsWith(READ_ONLY_FIELD_PREFIX);
// Labels only. The stored name keeps the `#`: it is both the read-only marker
// and the V4 property key.
export const fieldDisplayName = (name) =>
  typeof name === "string" && name.startsWith(READ_ONLY_FIELD_PREFIX)
    ? name.slice(READ_ONLY_FIELD_PREFIX.length).trimStart()
    : name;

// V4 keeps its system properties in `extraFields` too: those get the translated
// v3 label, custom fields keep the name the user gave them.
export const extraFieldLabel = (t, field) =>
  field?.system
    ? v4SystemPropertyLabel(t, field.name)
    : fieldDisplayName(field?.name);

// V4 `lastUpdate` is the connection-state timestamp (see mapV4DeviceToV3), not a
// last-signal time, so V4 devices label it connected/disconnected-since.
export const lastUpdateLabel = (t, device) => {
  if (!device.isFromV4) return t("lastActivity");
  return device.connected
    ? t("connectedSince", { defaultValue: "Connected since" })
    : t("disconnectedSince", { defaultValue: "Disconnected since" });
};

// Named V4 closed_reasons; any other value shows nothing.
const DISCONNECT_REASON_LABELS = new Map([
  [
    "requested_power_off",
    (t) =>
      t("disconnectReason.requestedPowerOff", {
        defaultValue: "Power off requested",
      }),
  ],
]);

// Appends a named V4 disconnect reason to `text`.
export const withDisconnectReason = (t, device, text) => {
  const label =
    device?.isFromV4 && !device.connected
      ? DISCONNECT_REASON_LABELS.get(device.get("disconnectReason"))
      : null;
  return label ? `${text} (${label(t)})` : text;
};

// Per-instance cache for `filterString(t)`. Auto-invalidates because each
// Record update produces a new instance.
const filterStringCache = new WeakMap();

export const configState = {
  neverConfigured: 0,
  configuring: 1,
  configured: 2,
  needsReboot: 3,
  willReboot: 4,
  error: 5,
};

const DeviceModel = Immutable.Record({
  id: undefined,
  name: "",
  accountName: "",
  arcKey: "",
  industry: "",
  tempAddress: "",
  tempAddressAutoClear: "",
  tempAddressExpiration: 0,
  operatingAddress: "",
  operatingLat: "",
  operatingLng: "",
  instructions: "",
  imei: undefined, //deprecated
  uniqueId: undefined,
  iccid: undefined,
  blemac: undefined,
  phone: undefined,
  status: undefined,
  activeState: undefined,
  configState: undefined,
  customConfig: false,
  lastUpdate: 0,
  created: 0,
  deleted: 0,
  beaconIds: [],
  locating: false,
  prom: undefined,
  safeword: "",
  panicword: "",
  mute: false,
  productName: "",
  accountId: 0,
  protocolId: null,
  deviceTypeId: 1,
  hasDevCon: true,
  trackingInterval: null,
  creatorId: 0,
  recording: null,
  os: "",
  version: 0,
  firmware: "",
  firmwareSupported: true,
  extraFields: [],
  urls: [],
  wifiOnly: false,
  ip: "",
  battery: null,
  batteryDate: null,

  wifi: false,
  srd: false,
  gnss: false,
  md: false,
  ble: false,
  fourG: false,
  threeG: false,
  voLTE: false,
  ripcord: false,
  compatibility: {},
  protocol: {},
  devconSectionsDisabled: [],
  previousPositions: [],

  locationForeground: false,
  locationBackground: false,
  locationPossible: "yes",

  apn: "",
  apnUser: "",
  apnPass: "",
  apnOperator: "",
  dns1: "",
  dns2: "",
  pin: "",

  moduleIds: [],

  _position: {},
  migratedV4: false,
  isMigratingV4: false,
  migrationError: null,
  consolidationError: null,
  isSynchronising: false,
  updatingSettingError: null,
  uuid: null,
  v4Type: null,
  v4Metadata: null,
  v4OwnerId: null,
  v4Capabilities: null,
  legacyId: undefined,
  // V4 availability flag (distinct from online). Defaults true so V3 devices,
  // which have no availability concept, are always treated as available.
  available: true,
  // V4 closed_reason while offline.
  disconnectReason: null,
});

export default class Device extends DeviceModel {
  id: number;
  name: string;
  accountName: string;
  industry: string;
  arcKey: string;
  tempAddress: string;
  tempAddressExpiration: number;
  tempAddressAutoClear: string;
  operatingAddress: string;
  operatingLat: number;
  lastUpdate: number;
  mute: boolean;
  operatingLng: number;
  instructions: string;
  imei: string; //deprecated
  uniqueId: string;
  customConfig: boolean;
  iccid: number;
  phone: number;
  status: string;
  activeState: number;
  created: number;
  deleted: number;
  beaconIds: number[];
  prom: string;
  accountId: number;
  hasDevCon: boolean;
  protocolId: number;
  deviceTypeId: number;
  trackingInterval: number;
  creatorId: number;
  recording: Object;
  os: string;
  version: number;
  firmware: string;
  firmwareSupported: boolean;
  extraFields: Object[];
  urls: Object[];
  locationForeground: boolean;
  locationBackground: boolean;
  locationPossible: string;
  wifiOnly: boolean;
  ip: string;
  battery: ?number;
  batteryDate: ?number;
  previousPositions: Position[];

  wifi: boolean;
  srd: boolean;
  gnss: boolean;
  ble: boolean;
  threeG: boolean;
  fourG: boolean;
  voLTE: boolean;
  ripcord: boolean;
  md: boolean;
  compatibility: any;
  protocol: any;
  devconSectionsDisabled: string[];

  apn: string;
  apnUser: string;
  apnPass: string;
  apnOperator: string;
  dns1: string;
  dns2: string;
  pin: string;
  migratedV4: boolean;
  isMigratingV4: boolean;
  migrationError: string;
  consolidationError: string;
  isSynchronising: boolean;
  updatingSettingError: string;
  uuid: string;

  moduleIds: number[];

  _position: {};

  static protocols = null;

  getProtocol() {
    return (Device.protocols || {})[this.deviceTypeId] || {};
  }

  get countsForLimit() {
    return (
      [
        deviceTypesInv.App,
        deviceTypesInv.WebUser,
        deviceTypesInv.TwigRemoteButton,
      ].indexOf(this.deviceTypeId) === -1
    );
  }

  get v4Brand() {
    return getV4Brand(this.get('v4Type'));
  }
  get isCueblyMobile() {
    // Brand-based check — covers current and future cuebly-mobile device types
    const brand = this.v4Brand;
    if (brand) return brand === 'cuebly-mobile';
    // Fallback when brand cache isn't loaded or device has no v4Type
    return this.deviceTypeId === deviceTypesInv.MobileAppLite;
  }
  get isMobileAppLite() {
    return this.isCueblyMobile;
  }
  get isV4Only() {
    const brand = this.v4Brand;
    if (brand) return isV4OnlyBrand(brand);
    // Fallback when brand cache isn't loaded or device has no v4Type
    return this.isCueblyMobile;
  }
  get isFromV4() {
    return !!this.get('v4Type');
  }
  get isEon() {
    return this.deviceTypeId === deviceTypesInv.TwigEon;
  }
  get hasWhitelist() {
    if (this.isTeltonika || this.isTwig) return true;
    return [deviceTypesInv.EviewEV04].indexOf(this.get("deviceTypeId")) >= 0;
  }
  get canTrack() {
    if (!this.can("tracking")) return false;
    if (this.isTwig) return this.gnss || this.srd || this.ble || this.wifi;
    return this.gnss || this.ble;
  }
  get canTrackIndoor() {
    if (this.isTwig) return this.srd || this.ble || this.wifi;
    return this.ble;
  }

  get canFilterWifiAndBluetoothSeparately() {
    if (this.isTwig)
      return isSupportedFirmware({
        type: DEVICE_TYPES.TWIG.NAME,
        fwVersion: this.get("firmware"),
        minVersion: "5.02.026",
        maxVersion: null,
      });
    return false;
  }

  get isWebUser() {
    return this.get("deviceTypeId") === deviceTypesInv.WebUser;
  }
  get isGenerated() {
    return (
      [deviceTypesInv.WebUser, deviceTypesInv.TwigRemoteButton].indexOf(
        this.get("deviceTypeId")
      ) >= 0
    );
  }

  get tempAddressClearObject() {
    if (!this.get("tempAddressAutoClear")) return { timeout: 24 };
    if (this.get("tempAddressAutoClear").indexOf("to") === 0) {
      return { timeout: +this.get("tempAddressAutoClear").replace("to", "") };
    } else if (this.get("tempAddressAutoClear").indexOf("time") === 0) {
      return { time: +this.get("tempAddressAutoClear").replace("time", "") };
    }
    return { timeout: +this.get("tempAddressAutoClear") };
  }

  get inError(): boolean {
    if (!this.isActive) return false;
    if (this.hasConfigError) return true;
    if (this.isApp) {
      if (!this.get("version")) return false;
      if (!this.isLocationPossible) return true;
      if (!this.locationPermitted) return true;
    }
    return this.hasFirmwareError;
  }

  get previousPositionsList() {
    if (!this.get("previousPositions")?.map) {
      return [];
    } else {
      const positions = [...this.get("previousPositions")];
      positions.shift();
      return positions;
    }
  }
  get hasFirmwareError(): boolean {
    return !this.get("firmwareSupported") && !!this.get("firmware");
  }

  get hasConfigError() {
    return this.get("configState") === configState.error;
  }

  get alarmHandler(): AlarmHandler {
    if (this.get("arcKey")) {
      return AlarmHandler.ARC;
    }
    return null;
  }

  get hasOsm() {
    return this.can("osm");
  }

  get isApp() {
    return this.deviceTypeId === deviceTypesInv.App;
  }
  get isEmerit() {
    return (
      [
        deviceTypesInv.EmeritConnect,
        deviceTypesInv.EmeritEWG100,
        deviceTypesInv.EmeritEWG200,
        deviceTypesInv.EmeritERG170,
        deviceTypesInv.EmeritERG360,
        deviceTypesInv.EmeritERG530,
        deviceTypesInv.EmeritERG650,
        deviceTypesInv.EmeritERG655,
        deviceTypesInv.EmeritEIS330,
        deviceTypesInv.EmeritEIS530,
        deviceTypesInv.EmeritEIS655,
      ].indexOf(this.deviceTypeId) >= 0
    );
  }
  // Emerit hardware. Excludes the Connect App, which has no procedure to launch.
  get isEmeritWatch() {
    return this.isEmerit && this.deviceTypeId !== deviceTypesInv.EmeritConnect;
  }
  get isApple() {
    return this.isApp && this.get("os") === "ios";
  }
  get isGeneric() {
    return this.deviceTypeId === deviceTypesInv.Generic;
  }

  can(functionality: string) {
    return !!(this.get("compatibility") || {})[functionality];
  }

  hasModule(modId: number) {
    return this.get("moduleIds").indexOf(modId) >= 0;
  }
  toggleModule(modId: number) {
    let mods = this.get("moduleIds");
    const i = this.get("moduleIds").indexOf(modId);
    i >= 0 ? mods.splice(i, 1) : mods.push(modId);
    return this.set("moduleIds", mods);
  }

  get alarmType() {
    switch (this.get("deviceTypeId")) {
      case deviceTypesInv.TeltonikaFMC640:
        return null;
      case deviceTypesInv.TeltonikaFMC650:
        return null;
      case deviceTypesInv.TeltonikaTMT250:
        return "deskAlarm";
      case deviceTypesInv.TeltonikaTMT250Old:
        return "deskAlarm";
      case deviceTypesInv.Generic:
        return "genericAlarm";
      default:
        return "personalAlarm";
    }
  }

  get canConfigureSimPin() {
    return this.isTeltonika;
  }

  get isIdle(): boolean {
    return this.get("status") === "idle";
  }
  get connected(): boolean {
    return this.get("status") === "online" || this.isIdle;
  }

  get isAvailable(): boolean {
    return this.get("available") !== false;
  }

  get color(): string {
    if (this.deviceTypeId === deviceTypesInv.Generic) return null;
    if (this.isIdle) return Colors.idle;
    return this.connected ? Colors.green : Colors.gray30;
  }

  getName(t): string {
    if (this.get("name") === "#account#") return getWhitelabel().platformName;
    if (this.get("name").indexOf("shadow_") === 0) return t("accountManager");
    return this.get("name");
  }
  deviceTypeName(t): string {
    const name = deviceTypes[this.get("deviceTypeId")]?.(t);
    if (name) return name;
    const v4Type = this.get("v4Type");
    if (v4Type) return prettifyV4Type(v4Type);
    consoleError(`Unknown device type '${this.get("deviceTypeId")}'`);
    return t("unknownDeviceType");
  }
  realDeviceTypeName(t): string {
    const name = realDeviceTypes[this.get("deviceTypeId")]?.(t);
    if (name) return name;
    const v4Type = this.get("v4Type");
    if (v4Type) return prettifyV4Type(v4Type);
    consoleError(`Unknown device type '${this.get("deviceTypeId")}'`);
    return t("unknownDeviceType");
  }
  // A V4 row's deviceTypeId can be a fallback (0, or App for cuebly-mobile).
  isSameTypeAs(other: Device): boolean {
    if (this.isFromV4 && other.isFromV4) {
      return this.get("v4Type") === other.get("v4Type");
    }
    const typeId = (d) =>
      d.isFromV4 ? getV3TypeIdForV4Type(d.get("v4Type")) : d.get("deviceTypeId");
    return !!typeId(this) && typeId(this) === typeId(other);
  }

  setIsOwnSimCard(isOwnSimcard) {
    if (isOwnSimcard) {
      return this.set("iccid", "");
    } else {
      return this.set("phone", "")
        .set("apn", "")
        .set("apnUser", "")
        .set("apnPass", "")
        .set("dns1", "")
        .set("dns2", "")
        .set("pin", "");
    }
  }
  setWifiOnly(wifiOnly) {
    if (!wifiOnly) return this.set("wifiOnly", !this.get("wifiOnly"));
    return this.setIsOwnSimCard(false)
      .set("iccid", "")
      .set("wifiOnly", !this.get("wifiOnly"));
  }

  get wifiOnlyPossible() {
    if (this.isTwigP5) {
      return !!this.get("wifi");
    }
    return this.isEmerit;
  }

  get hasMissingFields(): boolean {
    return (
      !this.isWebUser &&
      (!this.get("industry") || !this.get("operatingAddress"))
    );
  }

  get isActive(): boolean {
    return this.get("activeState") === activeState.active;
  }

  get isConfiguring(): boolean {
    if (this.isApp) return false;
    return (
      [configState.configuring, configState.willReboot].indexOf(
        this.get("configState")
      ) >= 0
    );
  }
  get configError(): boolean {
    return this.get("configState") === configState.error;
  }

  get tempAddressExpirationDate() {
    if (!this.tempAddressExpiration) return null;
    return dayjs.unix(this.tempAddressExpiration);
  }
  get lastUpdateTime(): dayjs {
    return dayjs.unix(this.lastUpdate || 0);
  }
  get lastUpdateTimeExport(): dayjs {
    return this.lastUpdate
      ? this.lastUpdateTime.format(sortableDateTimeFormat)
      : "";
  }

  get needsReboot(): boolean {
    return this.get("configState") === configState.needsReboot;
  }

  get position(): Position {
    return new Position(this.get("_position") || {});
  }

  get isFixedPosition(): boolean {
    return this.position.get("fixed") === true;
  }

  get hasFixedPosition(): boolean {
    if (!this.isFixedPosition) return false;
    const pos = this.position;
    return !!(pos.get("latitude") && pos.get("longitude"));
  }

  get isTwigP5() {
    return (
      [
        deviceTypesInv.TwigOne,
        deviceTypesInv.TwigOneEx,
        deviceTypesInv.TwigNeo,
        deviceTypesInv.TwigSOSCardP5,
        deviceTypesInv.TwigEasy5,
      ].indexOf(this.get("deviceTypeId")) >= 0
    );
  }
  get isTwig() {
    return getManufacturer(this.get("deviceTypeId")) === "twig";
  }
  // V4-native Twig: deviceTypeId is unmapped (0) so isTwig is false — detect via
  // brand, with a type-name fallback for before the registry cache is loaded.
  get isTwigV4() {
    if (!this.isFromV4) return false;
    if (this.v4Brand === "twig") return true;
    return /^twig/i.test(this.get("v4Type") || "");
  }
  // V3 or V4-native Twig; gates Twig UI regardless of the V3 type mapping.
  get isTwigDevice() {
    return this.isTwig || this.isTwigV4;
  }
  get isEV04() {
    return getManufacturer(this.get("deviceTypeId")) === "eview";
  }
  get isTeltonika() {
    return getManufacturer(this.get("deviceTypeId")) === "teltonika";
  }
  get canPowerOff() {
    if (this.isTwig) return true;
    // V4-native devices often have no V3 deviceTypeId (isTwig misses them);
    // gate on the V4 brand instead, with a v4Type-string fallback.
    if (this.isFromV4) {
      const brand = this.v4Brand;
      if (brand) return ["twig", "eview", "queclink"].includes(brand);
      return /^(twig|eview|queclink)/i.test(this.get("v4Type") || "");
    }
    return (
      [deviceTypesInv.EviewEV04, deviceTypesInv.QueclinkGL320M].indexOf(
        this.get("deviceTypeId")
      ) >= 0
    );
  }

  get operatingPosition(): Position {
    return new Position({
      latitude: this.operatingLat,
      longitude: this.operatingLng,
      address: this.operatingAddress,
    });
  }

  get locationPermitted(): boolean {
    if (!this.isApp) return true;
    return !!this.get("locationForeground") && !!this.get("locationBackground");
  }
  get isLocationPossible(): boolean {
    if (!this.isApp) return true;
    return this.get("locationPossible") !== "no";
  }

  get statusName(): string {
    if (this.deviceTypeId === deviceTypesInv.Generic) {
      return "noStatusAvailable";
    }
    if (this.isIdle) return "indeterminate";
    return this.connected ? "connected" : "notConnected";
  }

  constructor(props) {
    if (!props) props = {};
    props._position = props?.position || {};
    delete props.position;
    super(props);
  }

  get ripcordPossible() {
    return (
      [
        deviceTypesInv.TwigEmbody,
        deviceTypesInv.TwigNeo,
        deviceTypesInv.TwigSOSCard,
        deviceTypesInv.TwigSOSCardP5,
      ].indexOf(this.deviceTypeId) >= 0
    );
  }
  // ripcordPossible is deviceTypeId-based; V4 Twigs are unmapped, so also honour a
  // ripcord entry in the V4 capabilities map.
  get hasRipcordCapability() {
    if (this.ripcordPossible) return true;
    const caps = this.get("v4Capabilities");
    return !!caps && Object.prototype.hasOwnProperty.call(caps, "ripcord");
  }

  // Twig menu timer and app countdown. isTwigDevice also covers the V4-native
  // Twigs the V3 deviceTypeId misses, isApp the V3 app and a migrated V4 `app`.
  get hasTimerCapability() {
    return this.isTwigDevice || this.isApp;
  }

  // Alarms the device type settles on its own. Emerit needs no V4 variant —
  // every `emerit_*` type maps back to a real V3 Emerit deviceTypeId.
  raisesByDeviceType(trigger) {
    if (trigger === scenarioTriggers.timer) return this.hasTimerCapability;
    if (trigger === scenarioTriggers.lowPrioAlarm) return this.isEmerit;
    return false;
  }

  // V4 flattens an absent capability key to false, and only Twigs ever get one written
  // (eview reports `capabilities: {}`), so an unstated flag means "unknown" — read it
  // as capable. A Twig's explicitly written false still wins.
  hasCapabilityFlag(flag) {
    if (this.get(flag)) return true;
    if (!this.isFromV4) return false;
    return !isV4CapabilityKnown(this.get("v4Capabilities"), flag);
  }

  isCompatibleWithTrigger(trigger) {
    // V4-only devices (e.g. MobileAppLite) are notification endpoints
    // and can be used with any trigger type
    if (this.isV4Only) return true;
    const compatibility = Scenario.compatibility[this.deviceTypeId];
    // The matrix can't key a V4 row, so let the device type answer there. A V3
    // row with an entry still obeys it — the admin grid keeps the last word.
    if ((!compatibility || this.isFromV4) && this.raisesByDeviceType(trigger))
      return true;
    // The matrix only arrives over the V3 websocket, which a V4-native session
    // never connects — an absent entry is the normal case for a V4 row and
    // means "unknown", not "incompatible" (the pickers hide what they disable).
    if (!compatibility && !this.isFromV4) return false;
    if (compatibility && !compatibility[trigger]) return false;

    // Dependent on device specific settings
    if (trigger === scenarioTriggers.configError) return this.can("devCon");
    if (trigger === scenarioTriggers.manDown) return this.hasCapabilityFlag("md");
    if (trigger === scenarioTriggers.ripcord) return this.get("ripcord");
    if (trigger === scenarioTriggers.beaconBattery) return this.get("srd");
    if (trigger === scenarioTriggers.connectionChanged) {
      if (this.isTeltonika) return !!this.iccid;
    }
    return true;
  }

  get createdDate() {
    if (this.get("created")) return dayjs.unix(this.get("created"));
    return null;
  }
  get deletedDate() {
    if (this.get("deleted")) return dayjs.unix(this.get("deleted"));
    return null;
  }

  clone() {
    return this.set("id", this.id);
  }

  static create(creatorId: number, name: string = "", account: Account = null) {
    return new Device({
      name,
      creatorId: +creatorId,
      lastUpdate: dayjs().unix(),
      position: null,
      arcKey: "",
      deviceTypeId: 0,
      accountId: account ? account.id : null,
    });
  }

  get canReceiveAssistanceRequests() {
    return this.isApp && +this.get("version") >= 30;
  }

  filterString(t) {
    const cached = filterStringCache.get(this);
    if (cached && cached.t === t) return cached.value;
    const value = [
      this.id,
      this.name,
      this.deviceTypeName(t),
      this.ble,
      this.beaconIds,
      this.iccid,
      this.uniqueId,
      this.accountName,
      this.accountId,
      ...this.get("extraFields").map((f) => f.name + f.value),
    ]
      .join("")
      .lc();
    filterStringCache.set(this, { t, value });
    return value;
  }

  get BatteryIcon() {
    return (
      <DeviceBatteryIcon
        level={this.battery}
        lastUpdate={this.get("batteryDate")}
      />
    );
  }
}
